import { useCallback, useEffect, useRef, useState } from "react";
import { useSetAtom } from "jotai";
import { App as CapacitorApp } from "@capacitor/app";
import { tickerWSState } from "@/lib/store";
import { isOrtexApp } from "@/lib/utils";
import { scheduleIdle } from "@/lib/defer";

const TEN_SECONDS = 10000;

const multiUrl = () => `${process.env.NEXT_PUBLIC_MULTI_WS_URL}/stock_price/MULTI`;
const ortexUrl = () => `${process.env.NEXT_PUBLIC_ORTEX_WS_URL}/stock_price`;

// A desktop tab that is hidden briefly (switching tabs) keeps its sockets; one hidden for longer
// stops receiving prices it cannot show.
const HIDDEN_GRACE = 30000;

// True while the tab is visible and, in the Capacitor app, while the app is in the foreground.
const usePageActive = () => {
    const [visible, setVisible] = useState(true);
    const [appActive, setAppActive] = useState(true);

    useEffect(() => {
        let hiddenTimeout: NodeJS.Timeout | undefined;
        const onVisibilityChange = () => {
            clearTimeout(hiddenTimeout);
            if (document.hidden) hiddenTimeout = setTimeout(() => setVisible(false), HIDDEN_GRACE);
            else setVisible(true);
        };
        document.addEventListener("visibilitychange", onVisibilityChange);
        // A backgrounded app may have its timers frozen, so it closes straight away.
        const handle = isOrtexApp() ? CapacitorApp.addListener("appStateChange", ({ isActive }) => setAppActive(isActive)) : undefined;
        return () => {
            clearTimeout(hiddenTimeout);
            document.removeEventListener("visibilitychange", onVisibilityChange);
            handle?.then((h) => h.remove()).catch(() => undefined);
        };
    }, []);

    return visible && appActive;
};

// The sockets open once the page has loaded and the browser is idle, not while the first screen is
// still being built: every price message re-renders a ticker, and a stream of them during start-up
// competes with the page for the main thread. The tapes show the API's last prices until then. A
// page whose load event is slow (a large image, a stalled third party) still goes live after 5 s.
const LIVE_AT_THE_LATEST = 5000;

const useAfterLoad = () => {
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelIdle: () => void = () => undefined;
        const start = () => {
            clearTimeout(fallback);
            cancelIdle = scheduleIdle(() => setReady(true));
        };
        const fallback = setTimeout(() => setReady(true), LIVE_AT_THE_LATEST);
        if (document.readyState === "complete") start();
        else window.addEventListener("load", start, { once: true });
        return () => {
            clearTimeout(fallback);
            window.removeEventListener("load", start);
            cancelIdle();
        };
    }, []);

    return ready;
};

export function useAppWebSockets(userIdle: boolean) {
    const [multiWebSocket, setMultiWebSocket] = useState<WebSocket>();
    const [ortexWebSocket, setOrtexWebSocket] = useState<WebSocket>();
    const setTickerSubscriptions = useSetAtom(tickerWSState);
    const pageActive = usePageActive();
    const loaded = useAfterLoad();
    const active = loaded && !userIdle && pageActive;

    // The live sockets, read by the open/close helpers so they never act on a stale render
    const multiSocketRef = useRef<WebSocket>(undefined);
    const ortexSocketRef = useRef<WebSocket>(undefined);

    // Timeouts for reconnect logic - use refs to persist across renders
    const multiSocketTimeoutRef = useRef<NodeJS.Timeout>(undefined);
    const ortexSocketTimeoutRef = useRef<NodeJS.Timeout>(undefined);

    // Send hello messages
    const sendHelloMulti = useCallback(() => multiWebSocket?.send(JSON.stringify({ type: "hello" })), [multiWebSocket]);
    const sendHelloOrtex = useCallback(() => ortexWebSocket?.send(JSON.stringify({ type: "hello" })), [ortexWebSocket]);

    // Close sockets
    const closeMultiSocket = useCallback(() => multiWebSocket?.close(), [multiWebSocket]);
    const closeOrtexSocket = useCallback(() => ortexWebSocket?.close(), [ortexWebSocket]);

    // Open a socket, closing the previous one first so it is never orphaned. The ticker tape's
    // subscription list belongs to the old socket, so it is cleared for everyone to resubscribe.
    const openMultiSocket = useCallback(() => {
        const previous = multiSocketRef.current;
        if (previous) {
            previous.removeEventListener("close", resetMultiSocket);
            previous.close();
        }
        const socket = new WebSocket(multiUrl());
        multiSocketRef.current = socket;
        setTickerSubscriptions([]);
        setMultiWebSocket(socket);
    }, []);
    const openOrtexSocket = useCallback(() => {
        const previous = ortexSocketRef.current;
        if (previous) {
            previous.removeEventListener("close", resetOrtexSocket);
            previous.close();
        }
        const socket = new WebSocket(ortexUrl());
        ortexSocketRef.current = socket;
        setTickerSubscriptions([]);
        setOrtexWebSocket(socket);
    }, []);

    // Reset sockets (reconnect)
    const resetMultiSocket = useCallback(() => {
        if (multiSocketTimeoutRef.current) {
            clearTimeout(multiSocketTimeoutRef.current);
        }
        multiSocketTimeoutRef.current = setTimeout(openMultiSocket, TEN_SECONDS);
    }, []);
    const resetOrtexSocket = useCallback(() => {
        if (ortexSocketTimeoutRef.current) {
            clearTimeout(ortexSocketTimeoutRef.current);
        }
        ortexSocketTimeoutRef.current = setTimeout(openOrtexSocket, TEN_SECONDS);
    }, []);

    // Open both sockets while the user is active and the page is in the foreground; close them
    // when the user goes idle, the tab is hidden or the app is backgrounded, and reopen on return.
    useEffect(() => {
        if (active) {
            openMultiSocket();
            openOrtexSocket();
            return;
        }
        clearTimeout(multiSocketTimeoutRef.current);
        clearTimeout(ortexSocketTimeoutRef.current);
        if (multiSocketRef.current) {
            multiSocketRef.current.removeEventListener("close", resetMultiSocket);
            multiSocketRef.current.close();
            multiSocketRef.current = undefined;
            setMultiWebSocket(undefined);
        }
        if (ortexSocketRef.current) {
            ortexSocketRef.current.removeEventListener("close", resetOrtexSocket);
            ortexSocketRef.current.close();
            ortexSocketRef.current = undefined;
            setOrtexWebSocket(undefined);
        }
    }, [active]);

    // Set up WebSocket event listeners
    useEffect(() => {
        if (multiWebSocket) {
            multiWebSocket.readyState === 1 ? sendHelloMulti() : multiWebSocket.addEventListener("open", sendHelloMulti);
            multiWebSocket.addEventListener("error", closeMultiSocket);
            multiWebSocket.addEventListener("close", resetMultiSocket);
        }
        return () => {
            if (multiWebSocket) {
                multiWebSocket.removeEventListener("open", sendHelloMulti);
                multiWebSocket.removeEventListener("error", closeMultiSocket);
                multiWebSocket.removeEventListener("close", resetMultiSocket);
            }
        };
    }, [multiWebSocket, sendHelloMulti, closeMultiSocket, resetMultiSocket]);

    useEffect(() => {
        if (ortexWebSocket) {
            ortexWebSocket.readyState === 1 ? sendHelloOrtex() : ortexWebSocket.addEventListener("open", sendHelloOrtex);
            ortexWebSocket.addEventListener("error", closeOrtexSocket);
            ortexWebSocket.addEventListener("close", resetOrtexSocket);
        }
        return () => {
            if (ortexWebSocket) {
                ortexWebSocket.removeEventListener("open", sendHelloOrtex);
                ortexWebSocket.removeEventListener("error", closeOrtexSocket);
                ortexWebSocket.removeEventListener("close", resetOrtexSocket);
            }
        };
    }, [ortexWebSocket, sendHelloOrtex, closeOrtexSocket, resetOrtexSocket]);

    // Close the sockets and stop pending reconnects on unmount
    useEffect(() => {
        return () => {
            clearTimeout(multiSocketTimeoutRef.current);
            clearTimeout(ortexSocketTimeoutRef.current);
            multiSocketRef.current?.removeEventListener("close", resetMultiSocket);
            multiSocketRef.current?.close();
            ortexSocketRef.current?.removeEventListener("close", resetOrtexSocket);
            ortexSocketRef.current?.close();
        };
    }, []);

    return { multiWebSocket, ortexWebSocket };
}
